import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { batchLabel } from '../hooks/useBatches';
import { useImChannel } from '../hooks/useImChannel';
import { useIsMobile } from '../hooks/useMobile';

/**
 * 表彰提名（领导端，2026-05-12 新增）。
 *
 * 业务定位：与「晋升提名」（NominatorPage）是孪生兄弟，但两条链路完全独立：
 *   - 这里走 /recognition-nominator 而非 /nominator
 *   - quota 是单个数字（"领导本期可提名总数"），不分部门——与后端
 *     RecognitionLeaderAssignment.quota 字段一致；同一领导多条指派时后端取最小值
 *   - 入参字段：nomineeId + reason + recognitionType（可空） + detailDesc（可空）
 *     与晋升提名的 highlight / targetRank 不同
 *
 * 推送：HR 在「表彰提名指定」页发起钉钉推送，领导收到后点开本页直接填写。
 */

type QuotaResp = {
  /** 本人本期可表彰提名上限；null = 不限 */
  limit: number | null;
  /** 已提交数（不含已删除 / 草稿） */
  used: number;
  /** 剩余可填条数；limit 为 null 时也为 null（不限） */
  remaining: number | null;
  /** HR 在「表彰提名指定」给该领导填的所有部门名（用作钉钉文案与提示） */
  deptScopes: string[];
};

type RecognitionRow = {
  id: number;
  batchId: number;
  nomineeId: number;
  reason: string;
  detailDesc: string | null;
  recognitionType: string | null;
  submitAt: string | null;
  nominee: {
    id: number;
    name: string;
    position: string | null;
    deptPath: string | null;
  };
};

export function RecognitionNominatorPage() {
  const { label: imLabel } = useImChannel();
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const [batchId, setBatchId] = useState<number | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();

  const { data: batches = [] } = useQuery<any[]>({
    queryKey: ['batches-current'],
    queryFn: () => api.get('/batches'),
  });

  // 默认批次选择策略与晋升提名一致：当前 ONGOING（非沙箱）→ CALCULATING → 最新一条。
  useEffect(() => {
    if (batchId !== undefined) return;
    if (!batches.length) return;
    const pick =
      batches.find((b) => b.phase === 'ONGOING' && b.sandbox !== true) ??
      batches.find((b) => b.phase === 'CALCULATING') ??
      batches[0];
    if (pick) setBatchId(pick.id);
  }, [batches, batchId]);

  /** 后端在 quota 不存在时会抛 403——我们捕获并降级成"未被指派"。 */
  const {
    data: quota,
    isError: quotaError,
  } = useQuery<QuotaResp>({
    queryKey: ['recognition-quota', batchId],
    queryFn: () =>
      api.get('/recognition-nominator/quota', { params: { batchId } }),
    enabled: !!batchId,
    retry: false,
  });

  const { data: nominations = [] } = useQuery<RecognitionRow[]>({
    queryKey: ['my-recognitions', batchId],
    queryFn: () =>
      api.get('/recognition-nominator/nominations', { params: { batchId } }),
    enabled: !!batchId && !quotaError,
  });

  const { data: emps = [] } = useQuery<
    Array<{
      id: number;
      name: string;
      position: string | null;
      deptPath: string | null;
    }>
  >({
    queryKey: ['recognition-candidates'],
    queryFn: () => api.get('/recognition-nominator/candidates'),
  });

  const createMutation = useMutation({
    mutationFn: (vals: any) =>
      api.post('/recognition-nominator/nominations', { batchId, ...vals }),
    onSuccess: () => {
      message.success('已提交表彰提名');
      closeModal();
      qc.invalidateQueries({ queryKey: ['my-recognitions'] });
      qc.invalidateQueries({ queryKey: ['recognition-quota'] });
    },
    onError: (err: any) =>
      message.error(
        err?.response?.data?.message ?? err?.message ?? '提交失败',
      ),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      vals,
    }: {
      id: number;
      vals: {
        reason: string;
        recognitionType?: string;
        detailDesc?: string;
      };
    }) => api.patch(`/recognition-nominator/nominations/${id}`, vals),
    onSuccess: () => {
      message.success('已更新');
      closeModal();
      qc.invalidateQueries({ queryKey: ['my-recognitions'] });
    },
    onError: (err: any) =>
      message.error(
        err?.response?.data?.message ?? err?.message ?? '更新失败',
      ),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/recognition-nominator/nominations/${id}`),
    onSuccess: () => {
      message.success('已撤回');
      qc.invalidateQueries({ queryKey: ['my-recognitions'] });
      qc.invalidateQueries({ queryKey: ['recognition-quota'] });
    },
    onError: (err: any) =>
      message.error(
        err?.response?.data?.message ?? err?.message ?? '撤回失败',
      ),
  });

  function closeModal() {
    setModalOpen(false);
    setEditingId(null);
    form.resetFields();
  }

  function openCreate() {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  }

  function openEdit(row: RecognitionRow) {
    setEditingId(row.id);
    form.setFieldsValue({
      nomineeId: row.nomineeId,
      reason: row.reason ?? '',
      recognitionType: row.recognitionType ?? '',
      detailDesc: row.detailDesc ?? '',
    });
    setModalOpen(true);
  }

  /** 未被指派 = 后端 quota 抛错 / 没有任何活跃指派；前端等同"只读不允许新建"。 */
  const assigned = !quotaError && !!quota;

  const currentBatchName = useMemo(
    () => batchLabel((batches as any[]).find((b) => b.id === batchId)),
    [batches, batchId],
  );

  const statusBanner = useMemo(() => {
    if (!batchId) return null;
    if (!assigned) {
      return (
        <Alert
          type="info"
          message={`本批次（${currentBatchName}）您暂未被指派为表彰提名领导`}
          description={`如需开通，请联系 HR 在「表彰提名指定」页设置；HR 设置完成后会通过${imLabel}再次通知您。`}
          showIcon
          style={{ marginBottom: 16 }}
        />
      );
    }
    const limitText =
      quota?.limit == null ? '不限' : `${quota.used} / ${quota.limit}`;
    return (
      <Alert
        type="success"
        message={`HR 已下发本期表彰提名任务：${currentBatchName}（已用 ${limitText}）`}
        description={
          quota?.deptScopes?.length
            ? `主要部门范围：${quota.deptScopes.join(' / ')}（仅供参考，跨部门表彰允许）`
            : '请按本期上限选择要表彰的员工并填写理由。'
        }
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }, [assigned, batchId, currentBatchName, quota]);

  const reachedLimit =
    assigned &&
    quota?.limit != null &&
    (quota?.used ?? 0) >= quota.limit;

  return (
    <div>
      <div className="tg-page-title">表彰提名</div>
      {statusBanner}

      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="选择批次"
            style={{ width: 200 }}
            value={batchId}
            options={(batches as any[]).map((b) => ({
              label: batchLabel(b),
              value: b.id,
            }))}
            onChange={setBatchId}
          />
          <Button
            type="primary"
            onClick={openCreate}
            disabled={!batchId || !assigned || reachedLimit}
          >
            新建表彰
          </Button>
          {!batchId ? null : !assigned ? (
            <Tag color="default">未被指派</Tag>
          ) : reachedLimit ? (
            <Tag color="warning">已达上限</Tag>
          ) : (
            <Tag color="success">可填写</Tag>
          )}
        </Space>

        {assigned && (
          <Card
            title="本期使用情况"
            type="inner"
            style={{ marginBottom: 16 }}
          >
            <div style={{ marginBottom: 8 }}>
              <Progress
                percent={
                  quota?.limit != null && quota.limit > 0
                    ? Math.min(
                        100,
                        Math.round(((quota.used ?? 0) / quota.limit) * 100),
                      )
                    : 0
                }
                format={() =>
                  quota?.limit == null
                    ? `${quota?.used ?? 0} / 不限`
                    : `${quota?.used ?? 0} / ${quota.limit}`
                }
                style={{ width: 320 }}
              />
              {quota?.limit == null && (
                <Tag color="default" style={{ marginLeft: 8 }}>
                  未配置上限
                </Tag>
              )}
            </div>
          </Card>
        )}

        {isMobile ? (
          <List<RecognitionRow>
            dataSource={nominations}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="本期暂未提交任何表彰提名"
                />
              ),
            }}
            split={false}
            pagination={
              nominations.length > 6
                ? { pageSize: 6, showTotal: (n) => `共 ${n} 项` }
                : false
            }
            renderItem={(row) => {
              const dept =
                row.nominee?.deptPath?.split('/').slice(-2).join(' / ') ?? '—';
              const submitTime = row.submitAt
                ? new Date(row.submitAt).toLocaleString('zh-CN', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                  })
                : '—';
              return (
                <List.Item style={{ padding: '6px 0', borderBottom: 'none' }}>
                  <Card
                    size="small"
                    style={{
                      width: '100%',
                      borderRadius: 12,
                      border: '1px solid rgba(124,92,191,0.15)',
                      boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
                    }}
                    styles={{ body: { padding: 14 } }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 8,
                        gap: 8,
                      }}
                    >
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 16,
                            color: '#1f1f1f',
                            lineHeight: 1.3,
                          }}
                        >
                          {row.nominee?.name ?? '—'}
                        </div>
                        <div
                          style={{
                            color: '#94a3b8',
                            fontSize: 12,
                            marginTop: 4,
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                          }}
                        >
                          {dept} · {row.nominee?.position ?? '—'}
                        </div>
                      </div>
                      {row.recognitionType ? (
                        <Tag
                          color="gold"
                          style={{ marginRight: 0, flexShrink: 0 }}
                        >
                          {row.recognitionType}
                        </Tag>
                      ) : null}
                    </div>
                    <div
                      style={{
                        background: '#fffbe8',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 13,
                        color: '#475569',
                        lineHeight: 1.6,
                        marginBottom: 10,
                        wordBreak: 'break-word',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: '#94a3b8',
                          marginBottom: 4,
                        }}
                      >
                        表彰理由
                      </div>
                      {row.reason || '—'}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#94a3b8',
                        marginBottom: 10,
                      }}
                    >
                      提交时间：{submitTime}
                    </div>
                    <Space style={{ width: '100%' }} size={8} wrap>
                      <Button
                        size="middle"
                        onClick={() => openEdit(row)}
                        style={{ flex: 1, minWidth: 100 }}
                      >
                        修改
                      </Button>
                      <Popconfirm
                        title="撤回该条表彰？"
                        description="撤回后本期名额释放，可重新提名其他同事。"
                        okText="确认撤回"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => removeMutation.mutate(row.id)}
                      >
                        <Button
                          size="middle"
                          danger
                          style={{ flex: 1, minWidth: 100 }}
                        >
                          撤回
                        </Button>
                      </Popconfirm>
                    </Space>
                  </Card>
                </List.Item>
              );
            }}
          />
        ) : (
          <Table<RecognitionRow>
            rowKey="id"
            dataSource={nominations}
            columns={[
              {
                title: '被表彰人',
                dataIndex: ['nominee', 'name'],
                width: 120,
              },
              {
                title: '部门',
                dataIndex: ['nominee', 'deptPath'],
                ellipsis: true,
              },
              {
                title: '表彰类别',
                dataIndex: 'recognitionType',
                width: 140,
                render: (v: string | null) =>
                  v ? <Tag color="gold">{v}</Tag> : '—',
              },
              { title: '表彰理由', dataIndex: 'reason', ellipsis: true },
              {
                title: '提交时间',
                dataIndex: 'submitAt',
                width: 170,
                render: (t: string | null) =>
                  t
                    ? new Date(t).toLocaleString('zh-CN', { hour12: false })
                    : '-',
              },
              {
                title: '操作',
                key: 'op',
                width: 140,
                align: 'center',
                fixed: 'right',
                render: (_, row) => (
                  <Space size={4}>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => openEdit(row)}
                    >
                      修改
                    </Button>
                    <Popconfirm
                      title="撤回该条表彰？"
                      description="撤回后本期名额释放，可重新提名其他同事。"
                      okText="确认撤回"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => removeMutation.mutate(row.id)}
                    >
                      <Button type="link" size="small" danger>
                        删除
                      </Button>
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        title={editingId == null ? '新建表彰提名' : '修改表彰提名'}
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={isMobile ? '100vw' : 680}
        className={isMobile ? 'tg-fullscreen-modal' : undefined}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => {
            const reason = (v.reason ?? '').trim();
            const recognitionType =
              ((v.recognitionType ?? '') as string).trim() || undefined;
            const detailDesc =
              ((v.detailDesc ?? '') as string).trim() || undefined;
            if (editingId != null) {
              updateMutation.mutate({
                id: editingId,
                vals: { reason, recognitionType, detailDesc },
              });
            } else {
              createMutation.mutate({
                nomineeId: v.nomineeId,
                reason,
                recognitionType,
                detailDesc,
              });
            }
          }}
        >
          <Form.Item
            label="被表彰人"
            name="nomineeId"
            rules={[{ required: true, message: '请选择被表彰人' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="按姓名 / 职位 / 部门搜索"
              disabled={editingId != null}
              options={emps.map((e) => ({
                label: `${e.name} · ${e.position ?? '-'} · ${e.deptPath ?? '-'}`,
                value: e.id,
              }))}
            />
          </Form.Item>
          <Form.Item
            label="表彰类别"
            name="recognitionType"
            extra="可选；例如「卓越贡献」「年度新星」「跨部门协作」等自由文本"
          >
            <Input placeholder="例如：卓越贡献" maxLength={30} />
          </Form.Item>
          <Form.Item
            label="表彰理由"
            name="reason"
            extra="近半年突出贡献、量化成果，建议 ≥ 20 字"
            rules={[
              { required: true, message: '请填写表彰理由' },
              { min: 20, message: '请填写 ≥ 20 字的表彰理由' },
            ]}
          >
            <Input.TextArea
              rows={5}
              placeholder="例：在 2026Q1 万象城店冲刺中带队完成 320 万业绩，超额完成 30%，并培养出 3 名新晋一线咨询师。"
            />
          </Form.Item>
          <Form.Item label="补充说明（选填）" name="detailDesc">
            <Input.TextArea
              rows={3}
              placeholder="可补充更详细的背景、合议建议等"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
